const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';

interface ApiOptions {
  method?: string;
  body?: any;
  headers?: Record<string, string>;
}

// Enhanced error handling
class ApiError extends Error {
  status?: number;
  details?: any;

  constructor(message: string, status?: number, details?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

export async function api<T = any>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;
  
  // Retrieve token from storage
  const token = localStorage.getItem('auth_token') || 
                sessionStorage.getItem('auth_token');
  
  // Add authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // For POST/PUT/PATCH without body, send empty object to avoid Fastify error
  const needsBody = ['POST', 'PUT', 'PATCH'].includes(method);
  const requestBody = body !== undefined ? body : (needsBody ? {} : undefined);
  
  try {
    const res = await fetch(`${API_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: requestBody !== undefined ? JSON.stringify(requestBody) : undefined,
      credentials: 'include',
    });
    
    // Detailed error handling
    if (!res.ok) {
      let errorDetails;
      try {
        errorDetails = await res.json();
      } catch {
        errorDetails = null;
      }
      
      throw new ApiError(
        errorDetails?.error || errorDetails?.message || 'Request failed', 
        res.status, 
        errorDetails
      );
    }
    
    return res.json();
  } catch (error) {
    // Log detailed error information
    console.error('API Call Error:', {
      endpoint,
      method,
      error: error instanceof ApiError ? error.details : error
    });
    
    // Re-throw to allow caller to handle
    throw error;
  }
}

// Auth
export const auth = {
  signup: (data: { email: string; password: string; name?: string }) =>
    api('/api/auth/signup', { method: 'POST', body: data }),
  login: (data: { email: string; password: string }) =>
    api('/api/auth/login', { method: 'POST', body: data }),
  logout: () => api('/api/auth/logout', { method: 'POST' }),
  me: () => api('/api/auth/me'),
};

// Connections
export const connections = {
  list: (workspaceId: string) => api(`/api/connections?workspaceId=${workspaceId}`),
  test: (data: any) => api('/api/connections/test', { method: 'POST', body: data }),
  create: (data: any) => api('/api/connections', { method: 'POST', body: data }),
  getSchema: (id: string) => api(`/api/connections/${id}/schema`),
  delete: (id: string) => api(`/api/connections/${id}`, { method: 'DELETE' }),
};

// Chat response type
export interface ChatResponse {
  sessionId: string;
  messageId: string;
  answer: string;
  sql: string;
  vizSpec?: {
    mark?: string | { type: string; [key: string]: any };
    encoding?: Record<string, any>;
    echarts?: Record<string, any>;
    [key: string]: any;
  };
  rowCount: number;
  rows: any[];
  executionTimeMs: number;
  error?: string;
}

// Chat
export const chat = {
  sendMessage: (data: { connectionId: string; message: string; sessionId?: string }): Promise<ChatResponse> =>
    api('/api/chat/message', { method: 'POST', body: data }),
  getSessions: (connectionId: string) => api(`/api/chat/sessions?connectionId=${connectionId}`),
  getMessages: (sessionId: string) => api(`/api/chat/sessions/${sessionId}/messages`),
};

// Questions
export const questions = {
  list: (workspaceId: string, connectionId?: string) => {
    let url = `/api/questions?workspaceId=${workspaceId}`;
    if (connectionId) url += `&connectionId=${connectionId}`;
    return api(url);
  },
  create: (data: any) => api('/api/questions', { method: 'POST', body: data }),
  get: (id: string) => api(`/api/questions/${id}`),
  update: (id: string, data: any) => api(`/api/questions/${id}`, { method: 'PUT', body: data }),
  delete: (id: string) => api(`/api/questions/${id}`, { method: 'DELETE' }),
  run: (id: string, refresh?: boolean) => api(`/api/questions/${id}/run${refresh ? '?refresh=1' : ''}`, { method: 'POST' }),
};

// Analytics (OpenClaw Agent-powered)
export interface AnalyticsResponse {
  sessionId: string;
  messageId: string;
  answer: string;
  sql: string;
  vizSpec?: Record<string, any>;
  data: any[];
  rowCount: number;
  insights?: string[];
  followUpQuestions?: string[];
  executionTimeMs: number;
  agentUsed?: boolean;
  error?: string;
}

export const analytics = {
  analyze: (data: { connectionId: string; question: string; sessionId?: string; context?: string }): Promise<AnalyticsResponse> =>
    api('/api/analytics/analyze', { method: 'POST', body: data }),
  query: (data: { connectionId: string; sql: string }) =>
    api('/api/analytics/query', { method: 'POST', body: data }),
  insights: (data: { data: any[]; question?: string }) =>
    api('/api/analytics/insights', { method: 'POST', body: data }),
};

// Dashboards
export const dashboards = {
  list: (workspaceId: string) => api(`/api/dashboards?workspaceId=${workspaceId}`),
  create: (data: any) => api('/api/dashboards', { method: 'POST', body: data }),
  get: (id: string) => api(`/api/dashboards/${id}`),
  update: (id: string, data: any) => api(`/api/dashboards/${id}`, { method: 'PUT', body: data }),
  delete: (id: string) => api(`/api/dashboards/${id}`, { method: 'DELETE' }),
  addWidget: (id: string, data: any) => api(`/api/dashboards/${id}/widgets`, { method: 'POST', body: data }),
  updateWidget: (id: string, widgetId: string, data: any) =>
    api(`/api/dashboards/${id}/widgets/${widgetId}`, { method: 'PUT', body: data }),
  deleteWidget: (id: string, widgetId: string) =>
    api(`/api/dashboards/${id}/widgets/${widgetId}`, { method: 'DELETE' }),
  getPublic: (token: string) => api(`/api/dashboards/public/${token}`),
};